import { useState, useEffect, useRef } from 'react'
import { Filter, ChevronRight } from 'lucide-react'

export interface FilterState {
  organization: string | null
  status: string | null
  priority: string | null
}

interface FilterOption {
  value: string | null
  label: string
}

interface FilterDropdownProps {
  filters: FilterState
  onFilterChange: (filters: FilterState) => void
  organizations?: string[]
  statusOptions?: FilterOption[]
  showPriority?: boolean
  storageKey?: string
}

const DEFAULT_STATUS_OPTIONS = [
  { value: null, label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' }
]

const PRIORITY_OPTIONS = [
  { value: null, label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
]

export const FilterDropdown = ({
  filters,
  onFilterChange,
  organizations = [],
  statusOptions = DEFAULT_STATUS_OPTIONS,
  showPriority = true,
  storageKey = 'helpdesk-filters'
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Load filters from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(storageKey)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        onFilterChange(parsed)
      } catch (e) {
        console.error('Failed to parse saved filters:', e)
      }
    }
  }, [storageKey])

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(filters))
  }, [filters, storageKey])

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      setActiveSubmenu(null)
    }, 150)
  }

  const handleSubmenuEnter = (submenu: string) => {
    setActiveSubmenu(submenu)
  }

  const handleFilterSelect = (key: keyof FilterState, value: string | null) => {
    onFilterChange({
      ...filters,
      [key]: value
    })
  }

  const getActiveFilterCount = () => {
    let count = 0
    if (filters.organization) count++
    if (filters.status) count++
    if (filters.priority) count++
    return count
  }

  const activeCount = getActiveFilterCount()

  const organizationOptions = [
    { value: null, label: 'All Organizations' },
    ...organizations.map(org => ({ value: org, label: org }))
  ]

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Filter Button */}
      <button className="inline-flex items-center px-4 py-3 bg-white text-gray-700 border border-gray-200 text-sm font-medium rounded-full hover:bg-gray-50 transition-colors cursor-pointer">
        <Filter className="w-4 h-4 mr-2" />
        Filter
        {activeCount > 0 && (
          <span className="ml-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {activeCount}
          </span>
        )}
      </button>

      {/* Main Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {/* Organizations */}
          <div
            className="relative"
            onMouseEnter={() => handleSubmenuEnter('organization')}
          >
            <button
              className={`w-full px-4 py-2 text-sm text-left flex items-center justify-between hover:bg-gray-100 cursor-pointer ${
                filters.organization ? 'bg-[#AAAAAA]/30 rounded' : ''
              }`}
            >
              <span>Organizations</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>

            {/* Organizations Submenu */}
            {activeSubmenu === 'organization' && (
              <div className="absolute left-full top-0 ml-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                {organizationOptions.map((option) => (
                  <button
                    key={option.value || 'all'}
                    onClick={() => handleFilterSelect('organization', option.value)}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 cursor-pointer ${
                      filters.organization === option.value ? 'bg-[#AAAAAA]/30 rounded' : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Status */}
          <div
            className="relative"
            onMouseEnter={() => handleSubmenuEnter('status')}
          >
            <button
              className={`w-full px-4 py-2 text-sm text-left flex items-center justify-between hover:bg-gray-100 cursor-pointer ${
                filters.status ? 'bg-[#AAAAAA]/30 rounded' : ''
              }`}
            >
              <span>Status</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>

            {/* Status Submenu */}
            {activeSubmenu === 'status' && (
              <div className="absolute left-full top-0 ml-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                {statusOptions.map((option) => (
                  <button
                    key={option.value || 'all'}
                    onClick={() => handleFilterSelect('status', option.value)}
                    className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 cursor-pointer ${
                      filters.status === option.value ? 'bg-[#AAAAAA]/30 rounded' : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Priority - Only show if showPriority is true */}
          {showPriority && (
            <div
              className="relative"
              onMouseEnter={() => handleSubmenuEnter('priority')}
            >
              <button
                className={`w-full px-4 py-2 text-sm text-left flex items-center justify-between hover:bg-gray-100 cursor-pointer ${
                  filters.priority ? 'bg-[#AAAAAA]/30 rounded' : ''
                }`}
              >
                <span>Priority</span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>

              {/* Priority Submenu */}
              {activeSubmenu === 'priority' && (
                <div className="absolute left-full top-0 ml-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  {PRIORITY_OPTIONS.map((option) => (
                    <button
                      key={option.value || 'all'}
                      onClick={() => handleFilterSelect('priority', option.value)}
                      className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 cursor-pointer ${
                        filters.priority === option.value ? 'bg-[#AAAAAA]/30 rounded' : ''
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
